"use client"

import * as React from "react"

import { CheckIcon, MoreHorizontalIcon, PencilIcon, RotateCcwIcon, SendIcon, Undo2Icon, XIcon, type LucideIcon, CircleXIcon } from "lucide-react"

import {
  Badge,
  Button,
  ConfirmActionDialog,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Label,
  Select,
  type SelectOption,
  Textarea,
} from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { formatMoney, isZeroOrNegativeMoney } from "@/features/finance/lib/finance-utils"
import {
  availableTransferActions,
  financeScopeForBranchType,
  isDestructiveTransferAction,
  TransferAction,
} from "@/features/finance/lib/transfer-actions"
import {
  FinanceDirectoryStatusFilter,
  FinanceFundListStatus,
  type FinanceTransferRequestRecord,
} from "@/features/finance/model/finance-types"
import {
  useApproveFinanceTransferRequestMutation,
  useCancelFinanceTransferRequestMutation,
  useFinanceAccountsQuery,
  useFinanceFundsQuery,
  useRejectFinanceTransferRequestMutation,
  useReverseFinanceTransferRequestMutation,
  useSubmitFinanceTransferRequestMutation,
} from "@/features/finance/services/finance-query"
import { useApiErrorToast } from "@/shared/hooks/use-api-error"
import { getBranchDisplayLabel } from "@/shared/lib/branch/branch-display"
import { toast } from "sonner"
import { useIsMobile } from "@/shared/hooks/use-mobile"
import { MobileDetailScreen } from "@/shared/components/mobile-detail-screen"
import { RowActionsMenu, type RowActionsMenuItem } from "@/shared/components/row-actions-menu"
import {
  MobileFormSection,
  MobileFormSelectRow,
  MobileFormSheet,
  MobileFormTextArea,
} from "@/shared/components/mobile-form"

const ACTION_ICON: Record<TransferAction, LucideIcon> = {
  [TransferAction.Edit]: PencilIcon,
  [TransferAction.Submit]: SendIcon,
  [TransferAction.Approve]: CheckIcon,
  [TransferAction.Reject]: XIcon,
  [TransferAction.Cancel]: Undo2Icon,
  [TransferAction.Reverse]: RotateCcwIcon,
}

/**
 * The lifecycle actions for one transfer row.
 *
 * Which actions appear is decided by {@link availableTransferActions}, mirroring the backend's guards — the
 * sender submits and withdraws, the receiver approves and rejects. Approval is the one that cannot be a
 * single click: the receiving branch has to say WHERE the money lands and HOW it is designated, and only
 * they know that. Everything else is a confirm, because every one of them is either terminal or moves money.
 */
export function TransferRowActions({
  transfer,
  workspaceId,
  branchId,
  canEdit,
  onEdit,
  presentation = "menu",
}: {
  transfer: FinanceTransferRequestRecord
  workspaceId: string | null
  /** The branch currently being viewed — decides whether this row is ours to send or ours to approve. */
  branchId: string | null
  canEdit: boolean
  onEdit: (transfer: FinanceTransferRequestRecord) => void
  presentation?: "menu" | "buttons"
}) {
  const t = useTranslations("finance.transferRequests")
  const toastError = useApiErrorToast("finance")
  const isMobile = useIsMobile()

  const [openAction, setOpenAction] = React.useState<TransferAction | null>(null)
  const [reason, setReason] = React.useState("")
  const [destinationAccountId, setDestinationAccountId] = React.useState("")
  const [destinationFundId, setDestinationFundId] = React.useState("")

  const submit = useSubmitFinanceTransferRequestMutation()
  const approve = useApproveFinanceTransferRequestMutation()
  const reject = useRejectFinanceTransferRequestMutation()
  const cancel = useCancelFinanceTransferRequestMutation()
  const reverse = useReverseFinanceTransferRequestMutation()

  const actions = availableTransferActions({
    status: transfer.status,
    isSource: transfer.sourceBranchId === branchId,
    isDestination: transfer.destinationBranchId === branchId,
    isReversed: Boolean(transfer.reversedAt),
    canEdit,
  })

  /**
   * The receiving branch's accounts and funds — loaded only while the approval dialog is open, since a table
   * of twenty rows would otherwise fire forty queries for menus nobody opened.
   */
  const approvalOpen = openAction === TransferAction.Approve

  /**
   * BRANCH scope for a branch, PARISH scope for the system branch.
   *
   * The accounts and funds endpoints reject a BRANCH-scoped query that targets the system branch outright
   * ("Use PARISH scope for system-owned funds"), so hard-coding BRANCH left the parish — the side that
   * approves most transfers — with two 400s and two empty pickers instead of an approval it could complete.
   */
  const destinationScope = financeScopeForBranchType(transfer.destinationBranch?.branchType)

  const accountsResponse = useFinanceAccountsQuery(
    approvalOpen && workspaceId
      ? {
          workspaceId,
          branchId: transfer.destinationBranchId,
          scope: destinationScope,
          status: FinanceDirectoryStatusFilter.Active,
          page: 1,
          limit: 100,
          sortBy: "name",
          sortOrder: "asc",
          includeBalances: true,
        }
      : null,
  )
  const fundsResponse = useFinanceFundsQuery(
    approvalOpen && workspaceId
      ? {
          workspaceId,
          branchId: transfer.destinationBranchId,
          scope: destinationScope,
          status: FinanceFundListStatus.Active,
          page: 1,
          limit: 100,
        }
      : null,
  )

  const currency = transfer.currency
  const amountLabel = formatMoney(transfer.sourceAmount, currency)
  /**
   * The OTHER branch, from where we are standing. Approving names the sender ("Kicukiro is sending X");
   * withdrawing names the receiver. Hard-coding either one puts the viewer's own branch name in half the
   * dialogs, which reads as though they were sending money to themselves.
   */
  const counterparty = transfer.sourceBranchId === branchId ? transfer.destinationBranch : transfer.sourceBranch
  const counterpartyLabel = getBranchDisplayLabel({
    branchType: counterparty?.branchType,
    branchName: counterparty?.name ?? "",
    parishLabel: t("parish"),
    fallbackLabel: counterparty?.name ?? "",
  })

  /** Balance pills on both pickers, for the same reason the send form has them: a name alone is picking blind. */
  const accountOptions = React.useMemo<SelectOption[]>(
    () =>
      (accountsResponse.data?.items ?? []).map((account) => ({
        value: account.id,
        label:
          account.balance !== null ? (
            <span className='flex w-full items-center justify-between gap-3'>
              <span className='truncate'>{account.name}</span>
              <Badge
                variant={isZeroOrNegativeMoney(account.balance, currency) ? "destructive" : "success"}
                className='tabular-nums'
              >
                {formatMoney(account.balance, currency)}
              </Badge>
            </span>
          ) : (
            account.name
          ),
        textValue: account.name,
      })),
    [accountsResponse.data?.items, currency],
  )

  const fundOptions = React.useMemo<SelectOption[]>(
    () =>
      (fundsResponse.data?.items ?? []).map((fund) => ({
        value: fund.id,
        label: (
          <span className='flex w-full items-center justify-between gap-3'>
            <span className='truncate'>{fund.name}</span>
            <Badge
              variant={isZeroOrNegativeMoney(fund.balance, currency) ? "destructive" : "success"}
              className='tabular-nums'
            >
              {formatMoney(fund.balance, currency)}
            </Badge>
          </span>
        ),
        textValue: fund.name,
      })),
    [fundsResponse.data?.items, currency],
  )

  const closeDialog = React.useCallback(() => {
    setOpenAction(null)
    setReason("")
    setDestinationAccountId("")
    setDestinationFundId("")
  }, [])

  const run = React.useCallback(
    async (work: () => Promise<unknown>, successMessage: string) => {
      try {
        await work()
        toast.success(successMessage)
        closeDialog()
      } catch (error) {
        // The dialog stays OPEN on failure: the reason typed and the account chosen are still there to retry
        // with, and closing would make the user reconstruct both to find out what went wrong.
        toastError(error)
      }
    },
    [closeDialog, toastError],
  )

  if (actions.length === 0) return null

  const label = (action: TransferAction): string => {
    switch (action) {
      case TransferAction.Edit:
        return t("actions.edit")
      case TransferAction.Submit:
        return t("actions.submitDraft")
      case TransferAction.Approve:
        return t("actions.approve")
      case TransferAction.Reject:
        return t("actions.reject")
      case TransferAction.Cancel:
        return t("actions.withdraw")
      case TransferAction.Reverse:
        return t("actions.reverse")
    }
  }

  const pending =
    submit.isPending || approve.isPending || reject.isPending || cancel.isPending || reverse.isPending

  const openOrRun = (action: TransferAction) => {
    if (!actions.includes(action)) return
    // Edit is a navigation, not a decision — it needs no confirmation step.
    if (action === TransferAction.Edit) {
      onEdit(transfer)
      return
    }
    setOpenAction(action)
  }

  const buttonVariant = (action: TransferAction): React.ComponentProps<typeof Button>["variant"] => {
    switch (action) {
      case TransferAction.Approve:
        return "success"
      case TransferAction.Submit:
        return "default"
      case TransferAction.Reject:
        return "destructive"
      case TransferAction.Cancel:
      case TransferAction.Reverse:
        return "destructiveSubtle"
      case TransferAction.Edit:
        return "outline"
    }
  }

  return (
    <div className={presentation === "buttons" ? "flex flex-wrap items-center justify-end gap-2" : "text-right"}>
      {presentation === "buttons" ? (
        actions.map((action) => (
          <Button
            key={action}
            type='button'
            size='sm'
            variant={buttonVariant(action)}
            disabled={pending}
            onClick={() => openOrRun(action)}
          >
            {label(action)}
          </Button>
        ))
      ) : isMobile ? (
        // Phone: the shared actions sheet headed by the transfer, the refusing / undoing actions in their own group.
        <RowActionsMenu
          label={t("actions.label")}
          openLabel={t("actions.open")}
          sheetHeader={
            <span className='flex flex-col gap-0.5'>
              <span className='text-[15px] font-extrabold text-foreground tabular-nums'>{amountLabel}</span>
              <span className='text-xs font-semibold text-muted-foreground'>{counterpartyLabel}</span>
            </span>
          }
          groups={[
            actions
              .filter((action) => !isDestructiveTransferAction(action))
              .map<RowActionsMenuItem>((action) => ({ id: action, label: label(action), icon: ACTION_ICON[action], disabled: pending, onSelect: () => openOrRun(action) })),
            actions
              .filter((action) => isDestructiveTransferAction(action))
              .map<RowActionsMenuItem>((action) => ({
                id: action,
                label: label(action),
                icon: ACTION_ICON[action],
                destructive: true,
                disabled: pending,
                onSelect: () => openOrRun(action),
              })),
          ]}
        />
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='ghost' size='icon-sm'>
              <span className='sr-only'>{t("actions.open")}</span>
              <MoreHorizontalIcon />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            <DropdownMenuLabel>{t("actions.label")}</DropdownMenuLabel>
            {actions.map((action) => (
              <DropdownMenuItem
                key={action}
                className={isDestructiveTransferAction(action) ? "text-destructive focus:text-destructive" : undefined}
                onSelect={() => openOrRun(action)}
              >
                {label(action)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {isMobile ? (
        <>
          {/* Phone: approving picks an account and a fund (pickers open sheets), so it is a full screen. */}
          <MobileDetailScreen
            open={approvalOpen}
            onOpenChange={(next) => (next ? null : closeDialog())}
            title={t("approval.title")}
            backLabel={t("actions.cancelDialog")}
            showBackChevron={false}
            action={{
              label: t("actions.approve"),
              disabled: pending || !destinationAccountId || !destinationFundId,
              onPress: () =>
                !actions.includes(TransferAction.Approve)
                  ? undefined
                  : void run(
                      () => approve.mutateAsync({ id: transfer.id, destinationAccountId, destinationFundId }),
                      t("toasts.approved"),
                    ),
            }}
          >
            <p className='px-1 text-[15px] font-semibold text-muted-foreground'>
              {t("approval.description", { branch: counterpartyLabel, amount: amountLabel })}
            </p>
            <MobileFormSection>
              <MobileFormSelectRow
                doneLabel={t("form.mobile.done")}
                emptyLabel={t("form.mobile.noResults")}
                label={t("approval.account")}
                placeholder={t("form.mobile.choose")}
                value={destinationAccountId || undefined}
                onChange={(value) => setDestinationAccountId(value ?? "")}
                options={(accountsResponse.data?.items ?? []).map((account) => ({
                  value: account.id,
                  label: account.name,
                  description: account.balance !== null ? formatMoney(account.balance, currency) : undefined,
                }))}
                disabled={pending}
              />
              <MobileFormSelectRow
                doneLabel={t("form.mobile.done")}
                emptyLabel={t("form.mobile.noResults")}
                label={t("approval.fund")}
                placeholder={t("form.mobile.choose")}
                value={destinationFundId || undefined}
                onChange={(value) => setDestinationFundId(value ?? "")}
                options={(fundsResponse.data?.items ?? []).map((fund) => ({
                  value: fund.id,
                  label: fund.name,
                  description: formatMoney(fund.balance, currency),
                }))}
                disabled={pending}
              />
            </MobileFormSection>
          </MobileDetailScreen>

          {/* Phone: a refusal or a reversal is a reason and a red confirm, so the app's bottom sheet. */}
          <MobileFormSheet
            open={openAction === TransferAction.Reject || openAction === TransferAction.Reverse}
            onOpenChange={(next) => (next ? null : closeDialog())}
            title={openAction === TransferAction.Reverse ? t("reversal.title") : t("actions.reject")}
            note={openAction === TransferAction.Reverse ? t("reversal.description", { amount: amountLabel, branch: counterpartyLabel }) : undefined}
            cancelLabel={t("actions.cancelDialog")}
            saveLabel={openAction === TransferAction.Reverse ? t("actions.reverse") : t("actions.reject")}
            saving={pending}
            destructive
            onSave={() => {
              if (openAction === TransferAction.Reverse) {
                if (!actions.includes(TransferAction.Reverse)) return
                void run(() => reverse.mutateAsync({ id: transfer.id, reversalReason: reason.trim() || null }), t("toasts.reversed"))
                return
              }
              if (!actions.includes(TransferAction.Reject)) return
              void run(() => reject.mutateAsync({ id: transfer.id, rejectionReason: reason.trim() || null }), t("toasts.rejected"))
            }}
          >
            <MobileFormSection title={openAction === TransferAction.Reverse ? t("reversal.reason") : t("approval.rejectionReason")}>
              <MobileFormTextArea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder={
                  openAction === TransferAction.Reverse ? t("reversal.reasonPlaceholder") : t("approval.rejectionReasonPlaceholder")
                }
                aria-label={openAction === TransferAction.Reverse ? t("reversal.reason") : t("approval.rejectionReason")}
                disabled={pending}
              />
            </MobileFormSection>
          </MobileFormSheet>
        </>
      ) : (
        <>
      {/* APPROVE — the only action with a real decision in it. The receiving branch chooses the account the
          money lands in and the fund it is designated to; neither can be guessed on their behalf. */}
      <Dialog open={approvalOpen} onOpenChange={(next) => (next ? null : closeDialog())}>
        <DialogContent size='md'>
          <DialogHeader>
            <DialogTitle>{t("approval.title")}</DialogTitle>
          </DialogHeader>

          <p className='text-muted-foreground text-sm'>
            {t("approval.description", { branch: counterpartyLabel, amount: amountLabel })}
          </p>

          <div className='space-y-4 py-2'>
            <div className='space-y-2'>
              <Label htmlFor='transfer-approve-account'>{t("approval.account")}</Label>
              <Select
                value={destinationAccountId}
                onValueChange={setDestinationAccountId}
                options={accountOptions}
                placeholder={t("approval.accountPlaceholder")}
                disabled={pending}
                isSearchable
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='transfer-approve-fund'>{t("approval.fund")}</Label>
              <Select
                value={destinationFundId}
                onValueChange={setDestinationFundId}
                options={fundOptions}
                placeholder={t("approval.fundPlaceholder")}
                disabled={pending}
                isSearchable
              />
            </div>
          </div>

          <DialogFooter>
            <Button type='button' variant='outline' onClick={closeDialog} disabled={pending}>
              {t("actions.cancelDialog")}
            </Button>
            <Button
              type='button'
              // Both are required by the endpoint, so enabling this without them is a guaranteed 400.
              disabled={pending || !destinationAccountId || !destinationFundId}
              onClick={() =>
                !actions.includes(TransferAction.Approve)
                  ? null
                  :
                run(
                  () =>
                    approve.mutateAsync({
                      id: transfer.id,
                      destinationAccountId,
                      destinationFundId,
                    }),
                  t("toasts.approved"),
                )
              }
            >
              {t("actions.approve")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* REJECT — a refusal the sender has to act on, so the reason travels with it. */}
      <Dialog
        open={openAction === TransferAction.Reject}
        onOpenChange={(next) => (next ? null : closeDialog())}
      >
        <DialogContent size='sm'>
          <DialogHeader>
            <DialogTitle>{t("actions.reject")}</DialogTitle>
          </DialogHeader>

          <div className='space-y-2 py-2'>
            <Label htmlFor='transfer-reject-reason'>{t("approval.rejectionReason")}</Label>
            <Textarea
              id='transfer-reject-reason'
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={t("approval.rejectionReasonPlaceholder")}
              disabled={pending}
            />
          </div>

          <DialogFooter>
            <Button type='button' variant='outline' onClick={closeDialog} disabled={pending}>
              {t("actions.cancelDialog")}
            </Button>
            <Button
              type='button'
              variant='destructive'
              disabled={pending}
              onClick={() =>
                !actions.includes(TransferAction.Reject)
                  ? null
                  :
                run(
                  () => reject.mutateAsync({ id: transfer.id, rejectionReason: reason.trim() || null }),
                  t("toasts.rejected"),
                )
              }
            >
              {t("actions.reject")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* REVERSE — posts compensating entries against a transfer that already moved money, so the reason is
          worth capturing even though the endpoint treats it as optional. */}
      <Dialog
        open={openAction === TransferAction.Reverse}
        onOpenChange={(next) => (next ? null : closeDialog())}
      >
        <DialogContent size='sm'>
          <DialogHeader>
            <DialogTitle>{t("reversal.title")}</DialogTitle>
          </DialogHeader>

          <p className='text-muted-foreground text-sm'>
            {t("reversal.description", { amount: amountLabel, branch: counterpartyLabel })}
          </p>

          <div className='space-y-2 py-2'>
            <Label htmlFor='transfer-reverse-reason'>{t("reversal.reason")}</Label>
            <Textarea
              id='transfer-reverse-reason'
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={t("reversal.reasonPlaceholder")}
              disabled={pending}
            />
          </div>

          <DialogFooter>
            <Button type='button' variant='outline' onClick={closeDialog} disabled={pending}>
              {t("actions.cancelDialog")}
            </Button>
            <Button
              type='button'
              variant='destructive'
              disabled={pending}
              onClick={() =>
                !actions.includes(TransferAction.Reverse)
                  ? null
                  :
                run(
                  () => reverse.mutateAsync({ id: transfer.id, reversalReason: reason.trim() || null }),
                  t("toasts.reversed"),
                )
              }
            >
              {t("actions.reverse")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

        </>
      )}

      {/* SUBMIT and WITHDRAW are yes/no, so they use the SHARED confirm dialog rather than a bespoke one. */}
      <ConfirmActionDialog
        open={openAction === TransferAction.Submit}
        onOpenChange={(next) => {
          if (!next) closeDialog()
        }}
        title={t("confirm.submit.title")}
        description={t("confirm.submit.description", { amount: amountLabel, branch: counterpartyLabel })}
        confirmLabel={t("actions.submitDraft")}
        cancelLabel={t("actions.cancelDialog")}
        pending={pending}
        onConfirm={() =>
          actions.includes(TransferAction.Submit)
            ? void run(() => submit.mutateAsync(transfer.id), t("toasts.submitted"))
            : undefined
        }
      />

      <ConfirmActionDialog
        open={openAction === TransferAction.Cancel}
        onOpenChange={(next) => {
          if (!next) closeDialog()
        }}
        title={t("confirm.cancel.title")}
        description={t("confirm.cancel.description", { amount: amountLabel })}
        confirmLabel={t("actions.withdraw")}
        cancelLabel={t("actions.cancelDialog")}
        confirmVariant='destructive'
        icon={CircleXIcon}
        pending={pending}
        onConfirm={() =>
          actions.includes(TransferAction.Cancel)
            ? void run(() => cancel.mutateAsync({ id: transfer.id, cancellationReason: null }), t("toasts.cancelled"))
            : undefined
        }
      />
    </div>
  )
}
